import { LogLevel, LogEntry, LoggerOptions, LogTransport } from './types';
import { LOG_CONFIG } from './config';
import { LokiTransport } from './transports/LokiTransport';

export class Logger {
  private readonly minLogLevel: LogLevel;
  private readonly labels: Record<string, string>;
  private readonly transport: LogTransport;
  private readonly isProduction: boolean;

  constructor(options: LoggerOptions = {}) {
    this.minLogLevel = options.minLogLevel || LOG_CONFIG.general.minLogLevel;
    this.labels = {
      ...LOG_CONFIG.loki.labels,
      ...options.additionalLabels,
    };
    this.transport = options.transport || new LokiTransport();
    this.isProduction = import.meta.env.PROD === true;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = Object.values(LogLevel);
    return levels.indexOf(level) >= levels.indexOf(this.minLogLevel);
  }

  private sanitize(data: any): any {
    if (data === null || data === undefined) {
      return data;
    }

    if (typeof data !== 'string') {
      // For non-string data, we'll stringify it first if it's an object
      if (typeof data === 'object') {
        try {
          data = JSON.stringify(data);
        } catch (e) {
          // If we can't stringify, return the original
          return data;
        }
      } else {
        return data;
      }
    }

    let sanitized = data;
    LOG_CONFIG.redactionPatterns.forEach(({ pattern, replacement }) => {
      sanitized = sanitized.replace(pattern, replacement);
    });
    
    // If we stringified an object earlier, parse it back
    if (typeof data === 'object' && typeof sanitized === 'string') {
      try {
        return JSON.parse(sanitized);
      } catch (e) {
        // If we can't parse it back, return the sanitized string
        return sanitized;
      }
    }
    
    return sanitized;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    metadata?: Record<string, any>,
    error?: Error
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: this.sanitize(message),
      labels: this.labels,
    };

    if (metadata) {
      entry.metadata = Object.entries(metadata).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: this.sanitize(value),
        }),
        {}
      );
    }

    if (error) {
      entry.error = {
        name: error.name,
        message: this.sanitize(error.message),
        // Only include stack traces in development
        ...(this.isProduction ? {} : { stack: error.stack }),
      };
    }

    return entry;
  }

  private async log(
    level: LogLevel,
    message: string,
    metadata?: Record<string, any>,
    error?: Error
  ): Promise<void> {
    if (!this.shouldLog(level)) {
      return;
    }

    try {
      const entry = this.createLogEntry(level, message, metadata, error);

      // Log to console in non-production environments
      if (LOG_CONFIG.general.enableConsole) {
        const consoleMethod = level === LogLevel.ERROR ? 'error' :
          level === LogLevel.WARN ? 'warn' :
          level === LogLevel.INFO ? 'info' : 'debug';
        
        console[consoleMethod](
          `[${entry.timestamp}] ${level}:`,
          message,
          ...(metadata ? [metadata] : []),
          ...(error ? [error] : [])
        );
      }

      // Don't await in production to avoid performance impact
      if (this.isProduction) {
        this.transport.send(entry).catch(() => {
          // Silently fail in production
        });
        return Promise.resolve();
      } else {
        return this.transport.send(entry);
      }
    } catch (e) {
      // In production, we don't want logging to cause any issues
      if (!this.isProduction && LOG_CONFIG.general.enableConsole) {
        console.error('Error in logger:', e);
      }
      return Promise.resolve();
    }
  }

  debug(message: string, metadata?: Record<string, any>): Promise<void> {
    return this.log(LogLevel.DEBUG, message, metadata);
  }

  info(message: string, metadata?: Record<string, any>): Promise<void> {
    return this.log(LogLevel.INFO, message, metadata);
  }

  warn(message: string, metadata?: Record<string, any>): Promise<void> {
    return this.log(LogLevel.WARN, message, metadata);
  }

  error(message: string, error?: Error, metadata?: Record<string, any>): Promise<void> {
    return this.log(LogLevel.ERROR, message, metadata, error);
  }

  // Create a child logger with additional labels
  child(labels: Record<string, string>): Logger {
    return new Logger({
      minLogLevel: this.minLogLevel,
      additionalLabels: {
        ...this.labels,
        ...labels,
      },
      transport: this.transport,
    });
  }

  // Cleanup method to be called when the logger is no longer needed
  destroy(): void {
    try {
      if (this.transport instanceof LokiTransport) {
        this.transport.destroy();
      }
    } catch (e) {
      // Silently fail in production
      if (!this.isProduction && LOG_CONFIG.general.enableConsole) {
        console.error('Error destroying logger:', e);
      }
    }
  }
} 

